// src/utils/processLoginResponse.ts

import { setToken, setUser } from "../../auth/auth";
import type { UserModel, UserParametersModel } from "../../type/user";
import { normalizeParameters } from "../../utils/usersParameters";


interface ProcessLoginResponseOptions {
  data: unknown;
  navigate: (path: string) => void;
  onError: (message: string) => void;
}

/**
 * Parses a login API response, sets auth state, and navigates on success.
 * Returns `true` on success, `false` if the response was missing a token.
 *
 * Shared by the email/password login flow (Login.tsx) and the Google OAuth
 * flow (googleAuth.ts) — both receive the same `{ token, user }` shape.
 */
export const processLoginResponse = ({
  data,
  navigate,
  onError,
}: ProcessLoginResponseOptions): boolean => {
  const maybe = data as Record<string, unknown>;

  const token = typeof maybe.token === "string" ? maybe.token : null;
  const rawUser =
    typeof maybe.user === "object" && maybe.user !== null
      ? (maybe.user as Record<string, unknown>)
      : undefined;

  if (!token) {
    onError("Login response did not include a token.");
    return false;
  }

  setToken(token);

  if (rawUser) {
    const userData: UserModel = {
      ...(rawUser as Omit<UserModel, "parameters">),
      parameters: normalizeParameters(
        rawUser.parameters as string | UserParametersModel[] | undefined
      ),
    };
    setUser(userData);
  }

  navigate("/dictionaries");
  return true;
};